import { useMemo } from 'react';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { bankersRound, type Basis, type CalcMethod } from '@everplan/shared';

export interface ChargeFormulaEditorProps {
  value: {
    calcMethod: CalcMethod;
    rateBps: number | null;
    basis: Basis | null;
    flatAmount: number;
  };
  siblings: Array<{ id: string; description: string | null; type: string }>;
  selfId: string;
  availableSections: string[];
  packageContext: {
    lineItemTotal: number;
    addOnTotal: number;
    sectionTotals: Record<string, number>;
    siblingResolvedAmounts: Record<string, number>;
  };
  onChange: (next: {
    calcMethod: CalcMethod;
    rateBps: number | null;
    basis: Basis | null;
    flatAmount: number;
  }) => void;
}

interface BasisSelection {
  sections: string[];
  chargeIds: string[];
  includeSubtotal: boolean;
}

function basisToSelection(basis: Basis | null): BasisSelection {
  const empty: BasisSelection = { sections: [], chargeIds: [], includeSubtotal: false };
  if (!basis) return empty;

  function walk(b: Basis, acc: BasisSelection): void {
    if (b.kind === 'package_subtotal') {
      acc.includeSubtotal = true;
    } else if (b.kind === 'sections') {
      for (const s of b.sections) if (!acc.sections.includes(s)) acc.sections.push(s);
    } else if (b.kind === 'charges') {
      for (const id of b.chargeIds) if (!acc.chargeIds.includes(id)) acc.chargeIds.push(id);
    } else if (b.kind === 'composite') {
      for (const part of b.parts) walk(part, acc);
    }
  }
  const acc: BasisSelection = { sections: [], chargeIds: [], includeSubtotal: false };
  walk(basis, acc);
  return acc;
}

function selectionToBasis(sel: BasisSelection): Basis | null {
  const parts: Basis[] = [];
  if (sel.sections.length > 0) parts.push({ kind: 'sections', sections: sel.sections });
  if (sel.chargeIds.length > 0) parts.push({ kind: 'charges', chargeIds: sel.chargeIds });
  if (sel.includeSubtotal) parts.push({ kind: 'package_subtotal' });

  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0]!;
  return { kind: 'composite', parts };
}

function computeBasisPreview(
  basis: Basis,
  ctx: ChargeFormulaEditorProps['packageContext'],
): number {
  switch (basis.kind) {
    case 'package_subtotal':
      return ctx.lineItemTotal + ctx.addOnTotal;
    case 'sections': {
      let sum = 0;
      for (const s of basis.sections) sum += ctx.sectionTotals[s] ?? 0;
      return sum;
    }
    case 'charges': {
      let sum = 0;
      for (const id of basis.chargeIds) sum += ctx.siblingResolvedAmounts[id] ?? 0;
      return sum;
    }
    case 'composite': {
      let sum = 0;
      for (const part of basis.parts) sum += computeBasisPreview(part, ctx);
      return sum;
    }
  }
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function ChargeFormulaEditor({
  value,
  siblings,
  selfId,
  availableSections,
  packageContext,
  onChange,
}: ChargeFormulaEditorProps) {
  const visibleSiblings = useMemo(
    () => siblings.filter((s) => s.id !== selfId),
    [siblings, selfId],
  );

  const selection = useMemo(() => basisToSelection(value.basis), [value.basis]);

  const computedCents = useMemo(() => {
    if (value.calcMethod === 'flat') return value.flatAmount;
    if (value.rateBps == null || !value.basis) return value.flatAmount;
    const basisTotal = computeBasisPreview(value.basis, packageContext);
    return bankersRound((value.rateBps * basisTotal) / 10000);
  }, [value, packageContext]);

  const handleMethodChange = (method: CalcMethod) => {
    if (method === 'flat') {
      onChange({
        calcMethod: 'flat',
        rateBps: null,
        basis: null,
        flatAmount: value.flatAmount,
      });
    } else {
      onChange({
        calcMethod: 'percentage',
        rateBps: value.rateBps,
        basis: value.basis,
        flatAmount: value.flatAmount,
      });
    }
  };

  const handleRateChange = (raw: string) => {
    if (raw === '') {
      onChange({ ...value, rateBps: null });
      return;
    }
    const pct = Number(raw);
    if (Number.isNaN(pct)) return;
    onChange({ ...value, rateBps: Math.round(pct * 100) });
  };

  const handleFlatChange = (cents: number | undefined) => {
    onChange({ ...value, flatAmount: cents ?? 0 });
  };

  const updateSelection = (next: BasisSelection) => {
    onChange({ ...value, basis: selectionToBasis(next) });
  };

  const toggleSection = (section: string) => {
    const next: BasisSelection = { ...selection };
    if (selection.sections.includes(section)) {
      next.sections = selection.sections.filter((s) => s !== section);
    } else {
      next.sections = [...selection.sections, section];
    }
    updateSelection(next);
  };

  const toggleCharge = (id: string) => {
    const next: BasisSelection = { ...selection };
    if (selection.chargeIds.includes(id)) {
      next.chargeIds = selection.chargeIds.filter((c) => c !== id);
    } else {
      next.chargeIds = [...selection.chargeIds, id];
    }
    updateSelection(next);
  };

  const toggleSubtotal = () => {
    updateSelection({ ...selection, includeSubtotal: !selection.includeSubtotal });
  };

  const ratePctDisplay =
    value.rateBps == null ? '' : (value.rateBps / 100).toString();

  const hasBasis = value.basis != null;

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
      {/* Method */}
      <div className="flex items-center gap-4">
        <span className="text-xs font-medium text-muted-foreground">Method:</span>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="radio"
            name="calc-method"
            value="flat"
            checked={value.calcMethod === 'flat'}
            onChange={() => handleMethodChange('flat')}
          />
          Flat
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="radio"
            name="calc-method"
            value="percentage"
            checked={value.calcMethod === 'percentage'}
            onChange={() => handleMethodChange('percentage')}
          />
          Percentage of…
        </label>
      </div>

      {value.calcMethod === 'flat' && (
        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium text-muted-foreground">Amount:</Label>
          <CurrencyInput
            valueCents={value.flatAmount}
            onChangeCents={handleFlatChange}
            className="h-8 w-32"
            placeholder="0.00"
          />
        </div>
      )}

      {value.calcMethod === 'percentage' && (
        <>
          <div className="flex items-center gap-2">
            <Label htmlFor="formula-rate" className="text-xs font-medium text-muted-foreground">
              Rate:
            </Label>
            <Input
              id="formula-rate"
              aria-label="rate"
              type="number"
              step="0.01"
              value={ratePctDisplay}
              onChange={(e) => handleRateChange(e.target.value)}
              className="h-8 w-24"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Basis:</span>
            <div className="grid gap-1.5 rounded-md border border-border bg-background p-2">
              {availableSections.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Sections
                  </div>
                  {availableSections.map((section) => (
                    <label
                      key={section}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selection.sections.includes(section)}
                        onCheckedChange={() => toggleSection(section)}
                        aria-label={capitalize(section)}
                      />
                      <span>{capitalize(section)}</span>
                    </label>
                  ))}
                </div>
              )}

              {visibleSiblings.length > 0 && (
                <div className="space-y-1 border-t border-border pt-1.5">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Charges
                  </div>
                  {visibleSiblings.map((sib) => {
                    const label = sib.description ?? capitalize(sib.type);
                    const resolved = packageContext.siblingResolvedAmounts[sib.id];
                    return (
                      <label
                        key={sib.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Checkbox
                          checked={selection.chargeIds.includes(sib.id)}
                          onCheckedChange={() => toggleCharge(sib.id)}
                          aria-label={label}
                        />
                        <span>
                          {label}
                          {resolved != null && (
                            <span className="ml-1 text-muted-foreground">
                              ({formatCurrency(resolved)})
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="space-y-1 border-t border-border pt-1.5">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={selection.includeSubtotal}
                    onCheckedChange={toggleSubtotal}
                    aria-label="Package subtotal"
                  />
                  <span>Package subtotal</span>
                </label>
              </div>
            </div>
            {!hasBasis && (
              <p className="text-xs text-destructive">
                Select a basis for the percentage.
              </p>
            )}
          </div>
        </>
      )}

      <div className="border-t border-border pt-2 text-sm font-medium">
        {`Computed: ${formatCurrency(computedCents)}`}
      </div>
    </div>
  );
}
